<template>
  <div :class="`cart-bundle-item-price--${variant}`">
    <div v-if="showSingleValue">${{ cartBundle.bundleTotalPostDiscounts.toFixed(2) }}</div>
    <div
      v-else
      class="cart-bundle-item-price__discounted"
    >
      <div class="cart-bundle-item-price__pre-discount">
        ${{ cartBundle.bundleTotalPreDiscounts.toFixed(2) }}
      </div>
      <div class="cart-bundle-item-price__post-discount fw-bold">
        ${{ cartBundle.bundleTotalPostDiscounts.toFixed(2) }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import { CartBundleStorefrontDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

import { CartBundleItemPriceVariant } from './CartBundleItemPriceVariant'

export default defineComponent({
  name: 'CartBundleItemPrice',
  props: {
    cartBundle: {
      type: Object as PropType<CartBundleStorefrontDto>,
      required: true
    },
    // Defaults to the main cart's treatment so existing call sites are unaffected.
    variant: {
      type: String as PropType<CartBundleItemPriceVariant>,
      default: CartBundleItemPriceVariant.Stacked
    }
  },
  computed: {
    /**
     * One value rather than a struck-through original, in two cases:
     *
     * - There is no discount, so both totals read the same.
     * - The bundle was **granted by a promotion** and this is the flyout's `Inline` treatment, which
     *   the board draws as a single `$0.00` (`16001:29157`). A granted bundle can carry a non-zero
     *   pre-discount total, and striking it through would read as a discount the customer chose rather
     *   than a reward.
     *
     * Reward-ness is read off `sourcePromotionId` rather than taken as a prop: the component already
     * has the whole DTO, so asking a caller to re-tell it would be a second knob describing the same
     * fact. It is scoped to `Inline` because `Stacked` — the main cart and checkout — still strikes
     * reward bundles through today. Whether it should is a design question, not a cleanup.
     */
    showSingleValue(): boolean {
      const isPromotionGranted = !!this.cartBundle.sourcePromotionId

      return (
        (this.variant === CartBundleItemPriceVariant.Inline && isPromotionGranted) ||
        this.cartBundle.bundleTotalPreDiscounts === this.cartBundle.bundleTotalPostDiscounts
      )
    }
  }
})
</script>

<style scoped lang="scss">
.cart-bundle-item-price {
  &__pre-discount {
    text-decoration: line-through;
  }

  &__post-discount {
    color: $cady-color-magenta-600;
  }

  // Cart flyout: prices inline with a gap, post-discount black rather than magenta. Figma
  // `15980:29666` (desktop, 16px gap at 22px type) / `15927:54471` (mobile, 8px gap at 16px type).
  // The board omits the strikethrough on the mobile pre-discount node, but names it "Original Price"
  // and strikes its desktop twin — read as a board slip, so it is struck at both breakpoints.
  //
  // Weight and colour for the row come from `.cart-sidebar-row__price`, which owns the price type ramp
  // and inherits across the scope boundary; only the two genuine departures from it live here.
  &--inline {
    .cart-bundle-item-price__discounted {
      display: flex;
      align-items: baseline;
      gap: 0.5rem; // 8px

      @include media-breakpoint-up(md) {
        gap: 1rem; // 16px
      }
    }

    .cart-bundle-item-price__pre-discount {
      font-weight: 400;
      color: $cady-color-gray-600;
    }

    .cart-bundle-item-price__post-discount {
      color: $cady-color-black;
    }
  }
}
</style>
